import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNotify } from 'react-admin';
import {
    Grid,
    Box,
    Typography,
    Paper,
    Autocomplete,
    TextField,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Chip,
    Button,
    ButtonGroup,
    Tooltip as MuiTooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
} from 'recharts';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { StatCard } from './StatCard';

interface Course {
    id: number;
    title: string;
}

interface CourseDetailDashboardProps {
    dateParams: { from: string; to: string };
    groupBy: string;
    companyId?: number | null;
    departmentId?: number | null;
    groupId?: number | null;
}

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

export const CourseDetailDashboard: React.FC<CourseDetailDashboardProps> = ({
    dateParams,
    groupBy,
    companyId,
    departmentId,
    groupId,
}) => {
    const notify = useNotify();
    const [courses, setCourses] = useState<Course[]>([]);
    const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
    const [loading, setLoading] = useState(false);
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [data, setData] = useState<any>(null);

    const [downloading, setDownloading] = useState(false);
    const [regenerating, setRegenerating] = useState(false);

    const getToken = () => localStorage.getItem('token');

    const handleBulkCertificateRegenerate = async () => {
        if (!selectedCourse) return;
        if (!window.confirm('Alle Zertifikate für diesen Kurs neu generieren? Benutzernamen werden vom SSO aktualisiert.')) return;
        setRegenerating(true);
        try {
            const token = getToken();
            const query = companyId ? `?companyId=${companyId}` : '';
            const response = await fetch(`${API_URL}/certificates/bulk-regenerate/${selectedCourse.id}${query}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (response.ok) {
                const result = await response.json();
                notify(`${result.regeneratedCount} Zertifikat(e) erfolgreich neu generiert.`, { type: 'success' });
            } else {
                notify('Fehler beim Regenerieren der Zertifikate.', { type: 'error' });
            }
        } catch (err) {
            console.error('Error regenerating certificates:', err);
            notify('Fehler beim Regenerieren der Zertifikate.', { type: 'error' });
        } finally {
            setRegenerating(false);
        }
    };

    const handleParticipantListDownload = async () => {
        if (!selectedCourse) return;
        const token = getToken();
        if (!token) return;

        try {
            const response = await fetch(`${API_URL}/analytics/reports/export/pdf`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/pdf',
                },
                body: JSON.stringify({
                    reportType: 'participant-list',
                    entityId: selectedCourse.id,
                    filters: { from: dateParams.from, to: dateParams.to, groupBy, companyId, departmentId, groupId },
                }),
            });

            if (!response.ok) {
                notify('Teilnehmerliste konnte nicht erstellt werden.', { type: 'error' });
                return;
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Teilnehmerliste_${selectedCourse.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading participant list:', err);
            notify('Teilnehmerliste konnte nicht erstellt werden.', { type: 'error' });
        }
    };

    const handleBulkCertificateDownload = async (userProgress: any[]) => {
        if (!selectedCourse) return;
        setDownloading(true);
        try {
            const token = getToken();
            const params = new URLSearchParams();
            params.append('from', dateParams.from);
            params.append('to', dateParams.to);
            if (companyId) params.append('companyId', companyId.toString());
            if (departmentId) params.append('departmentId', departmentId.toString());
            if (groupId) params.append('groupId', groupId.toString());
            const query = params.toString() ? `?${params}` : '';
            const response = await fetch(`${API_URL}/certificates/bulk-download/${selectedCourse.id}${query}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Zertifikate_${selectedCourse.title.replace(/[^a-zA-Z0-9]/g, '_')}.zip`;
                a.click();
                URL.revokeObjectURL(url);
            } else {
                const problem = await response.json().catch(() => null);
                notify(problem?.error || 'Zertifikate konnten nicht heruntergeladen werden.', { type: 'error' });
            }
        } catch (err) {
            console.error('Error downloading certificates:', err);
            notify('Zertifikate konnten nicht heruntergeladen werden.', { type: 'error' });
        } finally {
            setDownloading(false);
        }
    };

    // Load available courses (own + licensed)
    const loadCourses = useCallback(async () => {
        const token = getToken();
        if (!token) return;

        setCoursesLoading(true);
        try {
            const params = new URLSearchParams();
            if (companyId) params.append('companyId', companyId.toString());
            params.append('itemsPerPage', '100');

            // Load own company courses
            const response = await fetch(`${API_URL}/courses?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/ld+json',
                },
            });

            let courseList: Course[] = [];
            if (response.ok) {
                const result = await response.json();
                courseList = (result['hydra:member'] || result.member || []).map((c: any) => ({
                    id: c.id,
                    title: c.title,
                }));
            }

            // Also load licensed courses (from marketplace)
            try {
                const licensedResponse = await fetch(`${API_URL}/licensed-courses`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json',
                    },
                });
                if (licensedResponse.ok) {
                    const licensedResult = await licensedResponse.json();
                    const ownIds = new Set(courseList.map(c => c.id));
                    const licensedCourses = (licensedResult.courses || [])
                        .filter((c: any) => !ownIds.has(c.id))
                        .map((c: any) => ({
                            id: c.id,
                            title: `${c.title} (Lizenz)`,
                        }));
                    courseList = [...courseList, ...licensedCourses];
                }
            } catch (e) {
                console.error('Error loading licensed courses:', e);
            }

            courseList.sort((a, b) => a.title.localeCompare(b.title));
            setCourses(courseList);
        } catch (err) {
            console.error('Error loading courses:', err);
        } finally {
            setCoursesLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        loadCourses();
    }, [loadCourses]);

    // Load course detail data
    const latestLoad = useRef(0);

    const loadCourseData = useCallback(async () => {
        const loadId = ++latestLoad.current;
        if (!selectedCourse) {
            setData(null);
            setLoading(false);
            return;
        }

        const token = getToken();
        if (!token) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                from: dateParams.from,
                to: dateParams.to,
                groupBy,
            });
            if (companyId) params.append('companyId', companyId.toString());
            if (departmentId) params.append('departmentId', departmentId.toString());
            if (groupId) params.append('groupId', groupId.toString());

            const response = await fetch(
                `${API_URL}/analytics/reports/course/${selectedCourse.id}?${params}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json',
                    },
                }
            );

            if (response.ok) {
                const result = await response.json();
                if (loadId !== latestLoad.current) return;
                setData(result);
            } else {
                if (loadId !== latestLoad.current) return;
                console.error('Failed to load course data');
                setData(null);
                notify('Kursdaten konnten nicht geladen werden.', { type: 'error' });
            }
        } catch (err) {
            if (loadId !== latestLoad.current) return;
            console.error('Error loading course data:', err);
            setData(null);
            notify('Kursdaten konnten nicht geladen werden.', { type: 'error' });
        } finally {
            if (loadId === latestLoad.current) {
                setLoading(false);
            }
        }
    }, [selectedCourse, dateParams, groupBy, companyId, departmentId, groupId, notify]);

    useEffect(() => {
        loadCourseData();
    }, [loadCourseData]);

    // Export handler
    const handleExport = async (exportFormat: 'pdf' | 'csv' | 'excel') => {
        if (!selectedCourse) return;

        const token = getToken();
        if (!token) return;

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/analytics/reports/export/${exportFormat}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': exportFormat === 'pdf' ? 'application/pdf' :
                             exportFormat === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
                             'text/csv',
                },
                body: JSON.stringify({
                    reportType: 'course-detail',
                    entityId: selectedCourse.id,
                    filters: { from: dateParams.from, to: dateParams.to, groupBy, companyId, departmentId, groupId },
                }),
            });

            if (!response.ok) {
                throw new Error('Export failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const ext = exportFormat === 'pdf' ? 'pdf' : exportFormat === 'excel' ? 'xlsx' : 'csv';
            a.download = `kurs-detail-${selectedCourse.id}.${ext}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Export error:', err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'in_progress':
                return 'primary';
            case 'not_started':
                return 'default';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed':
                return 'Abgeschlossen';
            case 'in_progress':
                return 'In Bearbeitung';
            case 'not_started':
                return 'Nicht gestartet';
            case 'enrolled':
                return 'Eingeschrieben';
            case 'failed':
                return 'Nicht bestanden';
            case 'suspended':
                return 'Pausiert';
            case 'pending_approval':
                return 'Warten auf Freigabe';
            case 'rejected':
                return 'Abgelehnt';
            default:
                return status;
        }
    };

    const completedInPeriod = (data?.userProgress ?? []).filter((u: any) => u.completed_in_period === true).length;

    return (
        <Box sx={{ px: 2 }}>
            {/* Course Selector */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flexGrow: 1, maxWidth: 500 }}>
                        <Typography variant="h6" gutterBottom>
                            Kurs auswählen
                        </Typography>
                        <Autocomplete
                            options={courses}
                            getOptionLabel={(option) => option.title}
                            value={selectedCourse}
                            onChange={(_, newValue) => setSelectedCourse(newValue)}
                            loading={coursesLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Kurs suchen"
                                    placeholder="Kurs eingeben..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {coursesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>
                    {selectedCourse && data && (
                        <ButtonGroup variant="outlined" size="small">
                            <Button
                                startIcon={<DownloadIcon />}
                                onClick={() => handleExport('pdf')}
                                disabled={loading}
                            >
                                PDF
                            </Button>
                            <Button
                                onClick={() => handleExport('csv')}
                                disabled={loading}
                            >
                                CSV
                            </Button>
                            <Button
                                onClick={() => handleExport('excel')}
                                disabled={loading}
                            >
                                Excel
                            </Button>
                        </ButtonGroup>
                    )}
                </Box>
            </Paper>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && !selectedCourse && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="textSecondary">
                        Bitte wählen Sie einen Kurs aus, um die Statistiken anzuzeigen.
                    </Typography>
                </Box>
            )}

            {!loading && selectedCourse && data && (
                <>
                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 1 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Einschreibungen"
                                value={data.summary?.total_enrollments || 0}
                                icon={<SchoolIcon sx={{ fontSize: 48 }} />}
                                color="primary"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Abgeschlossen"
                                value={data.summary?.completed || 0}
                                subtitle={`${parseFloat(data.summary?.completion_rate || 0).toFixed(1)}% Rate`}
                                icon={<CheckCircleIcon sx={{ fontSize: 48 }} />}
                                color="success"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Ø Fortschritt"
                                value={`${parseFloat(data.summary?.avg_progress || 0).toFixed(1)}%`}
                                icon={<TrendingUpIcon sx={{ fontSize: 48 }} />}
                                color="info"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Lektionen"
                                value={data.lessonStats?.length || 0}
                                icon={<MenuBookIcon sx={{ fontSize: 48 }} />}
                                color="warning"
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 4, fontStyle: 'italic' }}>
                        Gezählt werden alle, die im gewählten Zeitraum aktiv waren: eingeschrieben, gelernt oder abgeschlossen. Abschlüsse zählen nur, wenn sie im Zeitraum liegen. Pflichtaufgaben sind enthalten.
                    </Typography>

                    {/* Enrollment Trends */}
                    {data.trends && data.trends.length > 0 && (
                        <Paper sx={{ p: 3, mb: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                Einschreibungen über Zeit
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={data.trends}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="period" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="enrollments"
                                        name="Einschreibungen"
                                        stroke="#1976d2"
                                        strokeWidth={2}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="completions"
                                        name="Abschlüsse"
                                        stroke="#4caf50"
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>
                    )}

                    {/* Lesson Statistics */}
                    {data.lessonStats && data.lessonStats.length > 0 && (
                        <Paper sx={{ p: 3, mb: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                Lektions-Fortschritt
                            </Typography>
                            <ResponsiveContainer width="100%" height={Math.min(400, data.lessonStats.length * 40 + 100)}>
                                <BarChart data={data.lessonStats} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" domain={[0, 100]} unit="%" />
                                    <YAxis
                                        dataKey="lesson_title"
                                        type="category"
                                        width={200}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <Tooltip formatter={(value: any) => `${parseFloat(value).toFixed(1)}%`} />
                                    <Legend />
                                    <Bar
                                        dataKey="completion_rate"
                                        name="Abschlussrate"
                                        fill="#4caf50"
                                    />
                                    <Bar
                                        dataKey="avg_score"
                                        name="Durchschn. Score"
                                        fill="#1976d2"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    )}

                    {/* User Progress Table */}
                    {data.userProgress && data.userProgress.length > 0 && (
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                    Teilnehmer-Fortschritt
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <MuiTooltip title="Kompakte Namensliste mit Status aller Teilnehmenden im gewählten Zeitraum als PDF — z. B. zum Aushang oder als Teilnahmenachweis ohne einzelne Zertifikate.">
                                        <Button
                                            variant="outlined"
                                            startIcon={<DownloadIcon />}
                                            size="small"
                                            onClick={handleParticipantListDownload}
                                        >
                                            Teilnehmerliste drucken ({data.userProgress.length})
                                        </Button>
                                    </MuiTooltip>
                                    <MuiTooltip title={completedInPeriod === 0 ? 'Im gewählten Zeitraum hat niemand abgeschlossen.' : 'ZIP mit einem Zertifikat-PDF pro im Zeitraum abgeschlossenem Teilnehmer. Fehlende Zertifikate werden automatisch erstellt.'}>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                                                size="small"
                                                disabled={downloading || completedInPeriod === 0}
                                                onClick={() => handleBulkCertificateDownload(data.userProgress)}
                                            >
                                                {downloading ? 'Wird heruntergeladen...' : `Zertifikate herunterladen (${completedInPeriod})`}
                                            </Button>
                                        </span>
                                    </MuiTooltip>
                                    <MuiTooltip title="Erstellt alle bereits vorhandenen Zertifikate neu und synchronisiert die Namen aus dem SSO — sinnvoll nach Namensänderungen im Benutzerkonto.">
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            disabled={regenerating}
                                            onClick={handleBulkCertificateRegenerate}
                                        >
                                            {regenerating ? 'Wird generiert...' : 'Zertifikate neu generieren'}
                                        </Button>
                                    </MuiTooltip>
                                </Box>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nutzer</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Fortschritt</TableCell>
                                            <TableCell align="right">Score</TableCell>
                                            <TableCell>Eingeschrieben am</TableCell>
                                            <TableCell>Abgeschlossen am</TableCell>
                                            <TableCell>Letzte Aktivität</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.userProgress.map((user: any, index: number) => (
                                            <TableRow key={user.user_id || index}>
                                                <TableCell>
                                                    {user.user_name || `Nutzer #${user.user_id}`}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusLabel(user.status)}
                                                        color={getStatusColor(user.status) as any}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 150 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={parseFloat(user.progress_percentage || user.progress) || 0}
                                                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                                        />
                                                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                                                            {parseFloat(user.progress_percentage || user.progress || 0).toFixed(0)}%
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {user.score != null
                                                        ? `${parseFloat(user.score).toFixed(1)}%`
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {user.enrolled_at
                                                        ? new Date(user.enrolled_at).toLocaleDateString('de-DE')
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {user.completed_at
                                                        ? new Date(user.completed_at).toLocaleDateString('de-DE')
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {(user.last_accessed_at || user.last_activity)
                                                        ? new Date(user.last_accessed_at || user.last_activity).toLocaleDateString('de-DE')
                                                        : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </>
            )}
        </Box>
    );
};

export default CourseDetailDashboard;
